import React, { useState, useEffect, useRef } from 'react';
import Icon from '../shared/Icon';
import { apiService } from '../../../services/api';
import SplitButton from '../shared/SplitButton';
import PdfPages from './PdfPages';
import './DocumentViewerOverlay.css';

/**
 * Document view — the source document for the field being reviewed.
 *
 * PRD-1359 (Figma 1020-3796): this is the third step of one modal, NOT a modal
 * on top of a modal. It renders INSIDE the review modal's shell in place of the
 * review content, so there is a single backdrop and a single Escape target, and
 * the breadcrumb can walk back up: Dataset details › Review fields › Document
 * view. The caller owns the shell and the switch; this owns header, body and
 * footer only.
 *
 * The content endpoint is authenticated via the X-Access-Token header, and an
 * <iframe src> cannot carry headers — so the file is fetched as a blob and
 * wrapped in an object URL, the same approach ReviewMediaPanel uses for images.
 *
 * The WHOLE file is fetched (there is no per-page endpoint). PDFs are drawn
 * page by page with pdf.js (PdfPages), which is what lets the evidence page be
 * scrolled to reliably — the browser's own viewer ignored `#page=N` on blob:
 * URLs, and wrapped the document in its own toolbar and dark backdrop, neither
 * of which the design has. Images render directly; anything else falls back
 * to an iframe, where the page jump is best-effort and the banner says so.
 */
const DocumentViewerOverlay = ({
  datasetId, fileId, fileName, fieldLabel, value = '', page, datasetName, menu,
  onBack, onBackToDataset, onClose, onVerify, onCorrect, onCantVerify,
}) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | error
  // pdf.js could not load or draw this file (worker or decoder unreachable,
  // malformed PDF, …). The browser's own viewer then takes over in an iframe —
  // exactly what shipped before pdf.js — so a document is never a dead end.
  const [pdfFailed, setPdfFailed] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setPdfFailed(false);

    (async () => {
      const result = await apiService.fetchFileContentObjectUrl(datasetId, fileId, fileName);
      if (cancelled) {
        // Arrived after unmount — release it rather than leaking the blob.
        if (result?.url) URL.revokeObjectURL(result.url);
        return;
      }
      if (!result) {
        setStatus('error');
        return;
      }
      urlRef.current = result.url;
      setObjectUrl(result.url);
      setContentType(result.contentType || '');
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [datasetId, fileId, fileName]);

  // Same emptiness rule as stageValue, which refuses to stage an empty value.
  const hasValue = String(value ?? '').trim() !== '';
  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';
  // The banner is shown before the content type is known, so the file name
  // decides its copy up front (the same inference the fetch itself falls back
  // on) — otherwise it would flip wording once the document arrived.
  const looksPdf = isPdf || /\.pdf$/i.test(String(fileName || ''));
  // The new-tab link keeps the `#page=` fragment: a tab's viewer honours it.
  const src = objectUrl ? `${objectUrl}${looksPdf && page ? `#page=${page}` : ''}` : null;

  return (
    <React.Fragment>
      <div className="docreview-header">
        <div className="docreview-titlebar">
          <button
            type="button"
            className="docreview-back"
            aria-label="Back to review fields"
            onClick={onBack}
          >
            <Icon name="chevron_left" />
          </button>
          <h2 className="docreview-title">{datasetName || 'Document view'}</h2>
          {/* The same dataset context menu as the review step — the feedback doc
              asks for it on both, and it is the same dataset either way. */}
          {menu}
          <button className="docreview-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <nav className="docreview-breadcrumbs" aria-label="Breadcrumb">
          {onBackToDataset ? (
            <button type="button" className="resui-link" onClick={onBackToDataset}>
              Dataset details
            </button>
          ) : (
            <span className="docreview-crumb">Dataset details</span>
          )}
          <Icon name="chevron_right" className="docreview-crumb-sep" size={16} />
          <button type="button" className="resui-link" onClick={onBack}>
            Review fields
          </button>
          <Icon name="chevron_right" className="docreview-crumb-sep" size={16} />
          <span className="docreview-crumb" aria-current="page">Document view</span>
        </nav>
      </div>

      <div className="docreview-modal-body docviewer-body-wrap">
        <h4 className="docviewer-name">{fileName || 'Source document'}</h4>

        {/* The paging caveat belongs under the file name as an info banner, per
            the frame — it used to sit as loose text below the document, where a
            reviewer had already scrolled past the thing it explains.
            NOT gated on `status === 'ready'`: this is the only place the target
            page is stated, and it is needed most when the document does NOT
            load, so the reviewer can still go find the page themselves. */}
        {!isImage && (
          <div className="resui-banner resui-banner--info docviewer-note-banner">
            <Icon name="info_outline" />
            <span className="resui-banner-text">
              {/* Plain, not bold, and "below" — the banner sits ABOVE the
                  document now (review feedback 2026-10-06). PDFs drawn by us
                  open AT the evidence page, so the page-1 caveat is only for
                  the iframe — including when the iframe is the FALLBACK after
                  pdf.js failed, since the browser's viewer ignores `#page=` on
                  blob: URLs and the reviewer would otherwise land on page 1
                  with nothing telling them to scroll (PR #233 review). */}
              {page && looksPdf && !pdfFailed && (
                <>The evidence for this field is on page {page}. </>
              )}
              {page && (!looksPdf || pdfFailed) && (
                <>Some browsers open the file at page 1 regardless of the requested page — the
                evidence for this field is on page {page}. </>
              )}
              {/* The escape hatch lives here now, not in the footer (PRD-1455):
                  a tab renders formats and edge cases the iframe won't, and
                  honours #page= where the embedded viewer ignores it. Only
                  offered once there is a document to open. */}
              {status === 'ready' && (
                <>
                  If the document doesn’t appear below,{' '}
                  <a className="resui-link" href={src} target="_blank" rel="noopener noreferrer">
                    you can open the document in a new tab
                  </a>.
                </>
              )}
            </span>
          </div>
        )}

        <div className="docviewer-body">
          {status === 'loading' && (
            <p className="docviewer-state">Loading document…</p>
          )}
          {status === 'error' && (
            <p className="docviewer-state docviewer-state--error">
              Couldn’t load this document. It may still be processing, or you may not have
              access to it.
            </p>
          )}
          {status === 'ready' && isImage && (
            <img className="docviewer-image" src={objectUrl} alt={fileName || 'Source document'} />
          )}
          {status === 'ready' && isPdf && !pdfFailed && (
            <PdfPages url={objectUrl} page={page} fileName={fileName} onError={() => setPdfFailed(true)} />
          )}
          {status === 'ready' && !isImage && (!isPdf || pdfFailed) && (
            <iframe className="docviewer-frame" src={src} title={fileName || 'Source document'} />
          )}
        </div>
      </div>

      <div className="docviewer-foot">
        {/* Field only, no page number — Uthika's call on 2026-09-17, a deliberate
            deviation from both the frame and the feedback doc, which specify
            "Page [page number] - Field [field name]". Do not "restore" it from
            the design without asking. The page number still appears in the
            banner above, where it is load-bearing: it is the workaround for the
            browser ignoring the page jump. */}
        <div className="docviewer-ref">
          {fieldLabel && <p><strong>Field:</strong> {fieldLabel}</p>}
          {/* PRD-1455 (Figma 1020-3796): the value extraction inferred, so the
              reviewer can verify it against the document without going back. */}
          <p>
            <strong>Value:</strong>{' '}
            {hasValue ? value : <span className="docviewer-value-nil">nothing returned</span>}
          </p>
        </div>
        <div className="docviewer-actions">
          {/* Cancel steps back with no change, exactly like the breadcrumb. */}
          <button type="button" className="resui-btn resui-btn--secondary" onClick={onBack}>
            <span className="resui-btn-label">Cancel</span>
          </button>
          <SplitButton
            label="Verify and save"
            caretLabel="More verify options"
            onPrimary={onVerify}
            primaryLocked={!hasValue}
            options={[
              { label: 'Correct', icon: 'edit', onSelect: onCorrect },
              { label: 'Can’t verify', icon: 'block', onSelect: onCantVerify },
            ]}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default DocumentViewerOverlay;
